/**
 * Accepting an alert (PRD-0075 R-B5): into one of the case's investigations, or into a new one
 * named here. The alert's object and its source are pinned into that investigation's object set.
 *
 * Presentational: the feature reads the case's investigations and does the accept.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { LoadView } from './MonitorSection'

const DIALOG = 'cases.monitor.triage.dialog'
const NEW = 'new'

/** An investigation of the case an alert can go into; a locked one is shown, not offered. */
export interface InvestigationChoice {
  id: string
  title: string
  readOnly: boolean
}

export type AcceptTarget = { investigationId: string } | { newTitle: string }

/** The alerts being accepted (one, or a group); their name is the new investigation's default title. */
export interface AcceptRequest {
  alertIds: readonly string[]
  label: string
  pending: boolean
}

export interface AcceptAlertDialogProps {
  /** Null: the dialog is closed. */
  request: AcceptRequest | null
  choices: LoadView<InvestigationChoice>
  onConfirm: (alertIds: readonly string[], target: AcceptTarget) => void
  onClose: () => void
}

function TargetPicker({
  choices,
  value,
  onChange,
}: {
  choices: LoadView<InvestigationChoice>
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const rows = choices.status === 'ready' ? choices.rows : []
  const note =
    choices.status === 'loading'
      ? t(`${DIALOG}.loading`)
      : choices.status === 'failed'
        ? t(`${DIALOG}.loadError`)
        : null
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="accept-alert-target">{t(`${DIALOG}.target`)}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="accept-alert-target" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NEW}>{t(`${DIALOG}.newInvestigation`)}</SelectItem>
          {rows.map(choice => (
            <SelectItem key={choice.id} value={choice.id} disabled={choice.readOnly}>
              {choice.readOnly ? t(`${DIALOG}.locked`, { title: choice.title }) : choice.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  )
}

function AcceptForm({
  request,
  choices,
  onConfirm,
  onClose,
}: AcceptAlertDialogProps & { request: AcceptRequest }) {
  const { t } = useTranslation()
  const [target, setTarget] = useState(NEW)
  const [title, setTitle] = useState(request.label)
  const newTitle = title.trim()
  const ready = target !== NEW || newTitle.length > 0
  const confirm = () =>
    onConfirm(request.alertIds, target === NEW ? { newTitle } : { investigationId: target })
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={event => {
        event.preventDefault()
        if (ready && !request.pending) confirm()
      }}
    >
      <TargetPicker choices={choices} value={target} onChange={setTarget} />
      {target === NEW ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="accept-alert-title">{t(`${DIALOG}.newTitle`)}</Label>
          <Input
            id="accept-alert-title"
            value={title}
            maxLength={500}
            onChange={event => setTitle(event.target.value)}
          />
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t(`${DIALOG}.cancel`)}
        </Button>
        <Button type="submit" disabled={!ready || request.pending}>
          {t(`${DIALOG}.confirm`)}
        </Button>
      </DialogFooter>
    </form>
  )
}

export function AcceptAlertDialog(props: AcceptAlertDialogProps) {
  const { t } = useTranslation()
  const { request, onClose } = props
  return (
    <Dialog open={request !== null} onOpenChange={open => (open ? undefined : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`${DIALOG}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`${DIALOG}.description`, { name: request?.label ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {request ? <AcceptForm key={request.alertIds.join()} {...props} request={request} /> : null}
      </DialogContent>
    </Dialog>
  )
}
